import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import classNames from 'classnames'
import Slider from 'react-slick'

import {
    Tooltip,
    sendHoverSoundCommand,
    sendClickSoundCommand,
    openExternalBrowser,
} from 'react-wot-bootstrap'

import { openWindow } from 'react-wot-bootstrap/lib/helpers/web_to_client'

import { formatter } from 'helpers'
import { USER_ROLES } from 'helpers/i18n'

import { changeMode, changeOpenedAnnouncement } from 'state/actions/announcements'

import ButtonSimple from 'components/ButtonSimple/ButtonSimple'
import { ARROW_SIZES } from 'components/NavigationArrow/constants'

import {
    LOCKED_ANNOUNCEMENT_TOOLTIP,
    OUT_OF_CLAN,
    CREATE_NEW_ANNOUNCEMENT_TEXT,
    CREATE_NEW_ANNOUNCEMENT_BUTTON,
    CREATE_NEW_ANNOUNCEMENT_BUTTON_TOOLTIP,
    CREATE_NEW_ANNOUNCEMENT_ICON_TOOLTIP,
    EXTERNAL_URL_ICON_TOOLTIP,
    ANNOUNCEMENT_VIEW_BUTTON_CREATE_DISABLED_TOOLTIP,
} from './translations'

import Paginator from '../Paginator/Paginator'
import { Navigation } from './components/Navigation/Navigation'
import { CopyrightInfo } from './components/CopyrightInfo/CopyrightInfo'
import { AnnouncementOverlay } from './components/AnnouncementOverlay/AnnouncementOverlay'

import {
    ANNOUNCEMENT_MODE,
    NAVIGATION_DIRECTION,
    CARD_TYPES,
    TOOLTIP_IDS,
    AUTOPLAY_SPEED,
    SLIDE_SPEED,
} from './constants'

import { sortAnnouncementCards } from './helpers'

import { AnnouncementsStatusModel } from './AnnouncementsStatusModel'

import styles from './AnnouncementsCard.scss'

const announcementsStatus = new AnnouncementsStatusModel()

class AnnouncementsCard extends React.Component {
    static propTypes = {
        withQuests: PropTypes.bool,

        signInTokenUrl: PropTypes.string.isRequired,
        accountId: PropTypes.number.isRequired,

        items: PropTypes.array.isRequired,
        announcementMode: PropTypes.oneOf(Object.values(ANNOUNCEMENT_MODE)).isRequired,
        openedAnnouncement: PropTypes.number,
        hasPermissions: PropTypes.bool.isRequired,
        isAnnouncementsLimitReached: PropTypes.bool.isRequired,

        changeAnnouncementMode: PropTypes.func.isRequired,
        changeOpenedAnnouncement: PropTypes.func.isRequired,
    }

    state = {
        selectedItem: 0,
    }

    componentDidMount() {
        window.addEventListener('unload', this.saveReadAnnouncements)
        this.handleSliderBeforeChange(null, this.state.selectedItem)
        this.startAutoplay()
    }

    componentDidUpdate() {
        if (this.props.announcementMode === ANNOUNCEMENT_MODE.CARD) {
            this.startAutoplay()
        } else {
            this.stopAutoplay()
        }
    }

    componentWillUnmount() {
        window.removeEventListener('unload', this.saveReadAnnouncements)
        this.saveReadAnnouncements()
        this.stopAutoplay()
    }

    sliderRef = (c) => {
        return this.slider = c
    }

    stopAutoplay = () => {
        clearInterval(this.autoplayIntervalId)
    }

    startAutoplay = () => {
        this.stopAutoplay()

        if (this.props.items.length < 2) {
            return
        }

        if (!this.slider) {
            return
        }

        this.autoplayIntervalId = setInterval(this.slider.slickNext, AUTOPLAY_SPEED)
    }

    handleNavigation = (e, direction) => {
        e.stopPropagation()

        switch (direction) {
        case NAVIGATION_DIRECTION.LEFT:
            this.slider.slickPrev()
            break
        case NAVIGATION_DIRECTION.RIGHT:
            this.slider.slickNext()
            break
        default:
            break
        }
    }

    handleSliderBeforeChange = (oldItem, newSelectedItem) => {
        const item = this.props.items[newSelectedItem]
        announcementsStatus.markAsDisplayed(item)

        this.setState({
            selectedItem: newSelectedItem,
        })
    }

    saveReadAnnouncements = () => {
        announcementsStatus.markAllDisplayedAsRead()
        announcementsStatus.saveReadAnnouncements()
    }

    handleChangingMode = (mode, messageId) => {
        const { changeOpenedAnnouncement, changeAnnouncementMode } = this.props

        changeOpenedAnnouncement(messageId)
        changeAnnouncementMode(mode)
        this.saveReadAnnouncements()
    }

    closeAnnouncement = () => {
        this.handleChangingMode(ANNOUNCEMENT_MODE.CARD)
    }

    handleCreateNewAnnouncementClick = (e) => {
        e.stopPropagation()
        this.handleChangingMode(ANNOUNCEMENT_MODE.CREATE)
    }

    handleOpenAnnouncement = (announcementId) => {
        this.handleChangingMode(ANNOUNCEMENT_MODE.VIEW, announcementId)
    }

    handleOpenNews = ({ url, external_link }) => {
        this.saveReadAnnouncements()

        if (external_link) {
            openExternalBrowser(url)
        } else {
            openWindow('clan_ads_overlay', { url })
        }
    }

    renderContent = () => {
        return (
            <Slider
                draggable={false}
                initialSlide={this.state.selectedItem}
                beforeChange={this.handleSliderBeforeChange}
                arrows={false}
                speed={SLIDE_SPEED}
                ref={this.sliderRef}
                className={styles.cardSlider}
            >
                {this.props.items.map((item, index) => (
                    <div
                        key={`slide_${index}`}
                        className={styles.slideWrapper}
                    >
                        {this.renderSlide(item, index)}
                    </div>
                ))}
            </Slider>
        )
    }

    renderSlide = (item) => {
        switch (item.type) {
        case CARD_TYPES.ANNOUNCEMENTS:
            return this.renderAnnouncement(item)
        case CARD_TYPES.NEWS:
            return this.renderNews(item)
        case CARD_TYPES.CREATE_NEW_ANNOUNCEMENT:
            return this.renderCreateNewAnnouncement()
        default:
            return null
        }
    }

    renderNews = (news) => {
        return (
            <div
                className={styles.news}
                onMouseDown={sendClickSoundCommand}
                onMouseEnter={sendHoverSoundCommand}
                onClick={() => this.handleOpenNews(news)}
            >
                <h2 className={styles.newsTitle}>{news.title}</h2>
            </div>
        )
    }

    renderAnnouncement = (announcement) => {
        return (
            <div
                className={styles.announcement}
                onMouseDown={sendClickSoundCommand}
                onMouseEnter={sendHoverSoundCommand}
                onClick={() => this.handleOpenAnnouncement(announcement.message_id)}
            >
                <div className={styles.announcementContent}>
                    <div dangerouslySetInnerHTML={{ __html: formatter(announcement.body) }} />
                </div>
                <div className={styles.announcementDivider} />
                <CopyrightInfo
                    {...announcement}
                    accountId={this.props.accountId}
                />
            </div>
        )
    }

    renderCreateNewAnnouncement = () => {
        const announcementContentClassNames = classNames(
            styles.announcementContent,
            styles.announcementContent__new
        )

        return (
            <div
                className={styles.announcement}
                onMouseEnter={sendHoverSoundCommand}
            >
                <div className={announcementContentClassNames}>
                    {CREATE_NEW_ANNOUNCEMENT_TEXT}
                    <div className={styles.announcementButton}>
                        <ButtonSimple
                            isGlow
                            tooltipId={TOOLTIP_IDS.CREATE_NEW_ANNOUNCEMENT_BUTTON}
                            onClick={this.handleCreateNewAnnouncementClick}
                        >
                            {CREATE_NEW_ANNOUNCEMENT_BUTTON}
                        </ButtonSimple>
                    </div>
                </div>
            </div>
        )
    }

    renderCreateNewAnnouncementIcon = () => {
        const { items, hasPermissions, isAnnouncementsLimitReached } = this.props

        if (!this.props.hasPermissions) {
            return null
        }

        const activeSlide = items[this.state.selectedItem]
        const isAnnouncementSlideActive = activeSlide && activeSlide.type === CARD_TYPES.ANNOUNCEMENTS

        if (!isAnnouncementSlideActive || !hasPermissions) {
            return null
        }

        const tooltipId = isAnnouncementsLimitReached ?
            TOOLTIP_IDS.CREATE_NEW_ANNOUNCEMENT_DISABLED_ICON :
            TOOLTIP_IDS.CREATE_NEW_ANNOUNCEMENT_ICON

        const announcementIconClassNames = classNames(
            styles.announcementIcon,
            isAnnouncementsLimitReached && styles.announcementIcon__disabled,
        )

        return (
            <div
                className={announcementIconClassNames}
                data-tip data-for={tooltipId}
                onClick={(e) => !isAnnouncementsLimitReached && this.handleCreateNewAnnouncementClick(e)}
            />
        )
    }

    renderNewsExternalUrlIcon = () => {
        const activeSlide = this.props.items[this.state.selectedItem]
        const isNewsSlideActive = activeSlide && activeSlide.type === CARD_TYPES.NEWS

        if (!isNewsSlideActive || !activeSlide.external_link) {
            return null
        }

        return (
            <div
                className={styles.newsExternalUrlIcon}
                data-tip data-for={TOOLTIP_IDS.EXTERNAL_URL_ICON}
                onClick={() => this.handleOpenNews(activeSlide)}
            />
        )
    }

    renderNavigation = () => {
        const { items, withQuests } = this.props

        if (items.length < 2) {
            return null
        }

        return (
            <div className={styles.navigation}>
                <Navigation
                    disabledLeft={false}
                    disabledRight={false}
                    size={withQuests ? ARROW_SIZES.small : ARROW_SIZES.big}
                    onClick={this.handleNavigation}
                    onMouseEnter={this.slider ? this.slider.slickPause : null}
                    onMouseLeave={this.slider ? this.slider.slickPlay : null}
                />
                {items.length > 1 && (
                    <div className={styles.paginator}>
                        <Paginator
                            pagesCount={this.props.items.length}
                            currentPage={this.state.selectedItem}
                        />
                    </div>
                )}
            </div>
        )
    }

    renderAnnouncementOverlay = () => {
        const { items, accountId, openedAnnouncement } = this.props

        const announcements = items
            .filter((item) => item.type === CARD_TYPES.ANNOUNCEMENTS)

        const selectedAnnouncement = openedAnnouncement ?
            announcements.findIndex((a) => a.message_id === openedAnnouncement) :
            -1

        return (
            <div className={styles.announcementOverlay}>
                <AnnouncementOverlay
                    items={announcements}
                    currentSlide={selectedAnnouncement}
                    accountId={accountId}
                    onClose={this.closeAnnouncement}
                />
                {this.renderTooltips()}
            </div>
        )
    }

    /**
     * slick-slider inserts slides several times and browser throws errors about duplicate tooltips
     */
    renderTooltips() {
        const announcements = this.props.items.filter(i => i.type === CARD_TYPES.ANNOUNCEMENTS)
        const authorRoleTooltips = announcements.map(item => (
            <Tooltip id={`${TOOLTIP_IDS.AUTHOR_ROLE}${item.message_id}`} key={item.message_id}>
                {USER_ROLES[item.author_role]}
            </Tooltip>
        ))

        return (
            <div>
                <Tooltip id={TOOLTIP_IDS.LOCKED_ANNOUNCEMENT}>
                    {LOCKED_ANNOUNCEMENT_TOOLTIP}
                </Tooltip>
                <Tooltip id={TOOLTIP_IDS.OUT_OF_CLAN}>
                    {OUT_OF_CLAN}
                </Tooltip>
                <Tooltip id={TOOLTIP_IDS.CREATE_NEW_ANNOUNCEMENT_BUTTON}>
                    {CREATE_NEW_ANNOUNCEMENT_BUTTON_TOOLTIP}
                </Tooltip>
                <Tooltip id={TOOLTIP_IDS.CREATE_NEW_ANNOUNCEMENT_ICON}>
                    {CREATE_NEW_ANNOUNCEMENT_ICON_TOOLTIP}
                </Tooltip>
                <Tooltip id={TOOLTIP_IDS.CREATE_NEW_ANNOUNCEMENT_DISABLED_ICON}>
                    {ANNOUNCEMENT_VIEW_BUTTON_CREATE_DISABLED_TOOLTIP}
                </Tooltip>
                <Tooltip id={TOOLTIP_IDS.EXTERNAL_URL_ICON}>
                    {EXTERNAL_URL_ICON_TOOLTIP}
                </Tooltip>
                {authorRoleTooltips}
            </div>
        )
    }

    render() {
        const { items, withQuests, announcementMode } = this.props
        const { selectedItem } = this.state

        if (!items.length) {
            return null
        }

        if (announcementMode !== ANNOUNCEMENT_MODE.CARD) {
            return this.renderAnnouncementOverlay()
        }

        const item = items[selectedItem]

        const isNewMarker = !announcementsStatus.isRead(item)

        const baseClassNames = classNames(
            styles.base,
            withQuests && styles.base__withQuests
        )

        const customNewsBackgrounds = items
            .filter(item => item.type === CARD_TYPES.NEWS && item.image_url !== null)
            .map(item => (
                <div
                    className={classNames(
                        styles.newsBackground,
                        item.index === selectedItem && styles.newsBackground__active
                    )}
                    key={item.news_id}
                    style={{ backgroundImage: `url(${item.image_url})` }}
                />
            ))

        const defaultNewsBackgroundClassNames = classNames(
            styles.defaultNewsBackground,
            item.type === CARD_TYPES.NEWS && styles.defaultNewsBackground__active,
        )

        return (
            <div className={baseClassNames} onMouseEnter={this.stopAutoplay} onMouseLeave={this.startAutoplay}>
                <div className={defaultNewsBackgroundClassNames} />
                {customNewsBackgrounds}
                {this.renderContent()}
                {this.renderNavigation()}
                {this.renderCreateNewAnnouncementIcon()}
                {this.renderNewsExternalUrlIcon()}
                {isNewMarker && (
                    <div className={styles.newMarker} />
                )}
                {this.renderTooltips()}
            </div>
        )
    }
}

const stateToProps = (state) => {
    const announcements = state.announcements.list.map(item => ({
        type: CARD_TYPES.ANNOUNCEMENTS,
        ...item,
    }))

    const news = state.webbrg.clanNews.map(item => ({
        type: CARD_TYPES.NEWS,
        ...item,
    }))

    announcements.forEach(item => {
        if (item.is_read) {
            announcementsStatus.markAsRead(item)
        }
    })

    const items = sortAnnouncementCards([...announcements, ...news], announcementsStatus)

    const hasPermissions = state.announcements.settings.can_create_messages

    if (announcements.length === 0 && hasPermissions) {
        items.push({ type: CARD_TYPES.CREATE_NEW_ANNOUNCEMENT })
    }

    items.forEach((item, index) => item.index = index)

    return {
        signInTokenUrl: state.settings.signInTokenUrl,
        accountId: state.account.id,
        items,
        announcementMode: state.announcements.mode,
        openedAnnouncement: state.announcements.openedAnnouncement,
        hasPermissions: state.announcements.settings.can_create_messages,
        isAnnouncementsLimitReached: state.announcements.settings.is_limit_reached,
    }
}

const dispatchToProps = (dispatch) => ({
    changeAnnouncementMode: (mode) => dispatch(changeMode(mode)),
    changeOpenedAnnouncement: (announcementId) => dispatch(changeOpenedAnnouncement(announcementId)),
})

export default connect(stateToProps, dispatchToProps)(AnnouncementsCard)



// WEBPACK FOOTER //
// ./containers/ClanLanding/components/AnnouncementsCard/AnnouncementsCard.jsx